import { fetchSupabaseRows } from "@/lib/supabaseRest";

const typeLabels = {
  economico: "Accesible",
  local: "Local",
  bodega: "Bodega",
  terreno: "Terreno",
  oficina: "Oficina",
  casa: "Casa para negocio",
};

const normalizeList = (value) => (Array.isArray(value) ? value : []);

const hiddenSubmissionIds = new Set([
  "6f0b38fc-ad5a-4de7-af21-777a1b92842c",
]);

const splitList = (value = "") =>
  String(value || "")
    .split(/,|\n/)
    .map((item) => item.trim())
    .filter(Boolean);

const getSavedAdvantages = (value = "") =>
  String(value || "")
    .split(/\r?\n/)
    .map((item) => item.trim().replace(/^[-•\d.)\s]+/, "").trim())
    .filter(Boolean)
    .slice(0, 10);

const getBathroomCount = (services = "") => {
  const bathroomService = splitList(services).find((service) =>
    /\bba(?:ñ|n)o(?:s)?\b/i.test(service)
  );
  if (!bathroomService) return "";

  const count = bathroomService.match(/\d+/)?.[0];
  return count || "1";
};

const getZoneType = (submission) => {
  const text = [
    submission.ubicacion,
    submission.colonia,
    submission.descripcion,
    submission.negocios_misma_plaza,
    submission.ventajas,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const hasIndustrial = /industrial|bodega|nave|patio de maniobras|trailer|carga/.test(text);
  const hasResidential = /habitacional|residencial|vivienda|casas|departamentos|colonia|vecinos|familias|escuela/.test(text);
  const hasCommercial = /comercial|comercio|negocio|local|plaza|avenida|boulevard|blvd|flujo|publico|público|oficina|consultorio|ermita|esquina/.test(text);

  if (hasIndustrial && hasCommercial) return "Comercial e industrial";
  if (hasCommercial && hasResidential) return "Comercial y habitacional";
  if (hasIndustrial) return "Industrial";
  if (hasCommercial) return "Comercial";
  if (hasResidential) return "Habitacional";
  return "";
};

const isTestSubmission = (submission) => {
  if (hiddenSubmissionIds.has(submission.id)) return true;

  const text = [
    submission.nombre,
    submission.ubicacion,
    submission.descripcion,
    submission.negocios_misma_plaza,
    submission.notas_admin,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return text.includes("jhjhjh");
};

const removeBathroomFromServices = (services = "") =>
  splitList(services)
    .filter((service) => !/\bba(?:ñ|n)o(?:s)?\b/i.test(service))
    .join(", ");

const buildStreetLocation = (submission) => {
  const street = [submission.calle, submission.numero].filter(Boolean).join(" ");
  if (street) return street;
  if (submission.colonia && submission.ubicacion && submission.colonia !== submission.ubicacion) {
    return `${submission.ubicacion}, ${submission.colonia}`;
  }
  return submission.ubicacion || submission.colonia || "";
};

const normalizeLocationText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const mapSubmissionToProperty = (submission) => {
  const images = normalizeList(submission.imagenes);
  const videos = normalizeList(submission.videos).map((url) => ({
    src: url,
    type: "video",
  }));
  const type = submission.tipo || "local";
  const typeLabel = typeLabels[type] || "Propiedad";
  const location = String(submission.ubicacion || "").trim();
  const streetLocation = buildStreetLocation(submission);
  const neighborhood = submission.colonia || "";
  const city =
    submission.ciudad ||
    submission.municipio ||
    submission.alcaldia ||
    submission.delegacion ||
    "";
  const state = submission.estado || submission.entidad || "";
  const locationText = normalizeLocationText(
    [
      submission.ubicacion,
      submission.colonia,
      submission.calle,
      city,
      state,
      submission.descripcion,
    ]
      .filter(Boolean)
      .join(" ")
  );
  const isCoyoacanOffice =
    type === "oficina" && (locationText.includes("coyoacan") || locationText.includes("auriga"));
  const cleanServices = removeBathroomFromServices(submission.servicios);
  const savedAdvantages = getSavedAdvantages(submission.ventajas);
  const zoneType = getZoneType(submission);
  const details = {
    medidas: submission.medidas || "",
    frente: submission.frente || "",
    fondo: submission.fondo || "",
    metros: submission.metros || "",
    altura: submission.altura || "",
    nivel: submission.nivel || "",
    estacionamiento: submission.estacionamiento || "",
    banos: getBathroomCount(submission.servicios),
    ventajasRapidas: savedAdvantages,
    ventajas: savedAdvantages.join("\n"),
  };

  return {
    id: `supabase-${submission.id}`,
    submissionId: submission.id,
    source: "supabase",
    plan: submission.plan || "gratis",
    type,
    ribbon: typeLabel,
    status: isCoyoacanOffice ? "Recién agregado" : "Nuevo",
    title: `${typeLabel} en renta`,
    subtitle: "",
    price: Number(submission.precio || 0),
    location: streetLocation,
    address: neighborhood,
    city,
    state,
    lat: submission.lat,
    lng: submission.lng,
    mapUrl: submission.map_url || "",
    description: submission.descripcion || "",
    images: [...images, ...videos],
    videos,
    contrato: submission.contrato || "Mínimo 1 año",
    zona: submission.zona_tipo || zoneType || neighborhood || location,
    zonaDetalle: neighborhood || location,
    servicios: cleanServices || submission.servicios || "",
    estado: "Buen estado",
    disponibilidad: submission.disponibilidad || "",
    tiempoGracia: "Sí",
    requisitosRenta: "Mes de renta, mes de depósito, fiador y documentación",
    restricciones: submission.restricciones || "",
    negociosMismaPlaza: submission.negocios_misma_plaza || "",
    visionAnalysis: submission.vision_analysis || {},
    ventajas: savedAdvantages.join("\n"),
    medidas: submission.medidas || "",
    details,
    detalles: details,
    contacto: {
      nombre: submission.nombre || "",
      telefono: submission.telefono || "",
      correo: submission.correo || "",
    },
  };
};

export const getPublishedSubmissions = async () => {
  const query = new URLSearchParams({
    select: "*",
    estado_publicacion: "eq.publicada",
    order: "publicada_en.desc.nullslast,created_at.desc",
  });

  const rows = await fetchSupabaseRows("property_submissions", query.toString());
  return rows.filter((row) => !isTestSubmission(row)).map(mapSubmissionToProperty);
};

export const getPublishedSubmissionById = async (id) => {
  const query = new URLSearchParams({
    select: "*",
    id: `eq.${id}`,
    estado_publicacion: "eq.publicada",
    limit: "1",
  });

  const rows = await fetchSupabaseRows("property_submissions", query.toString());
  const row = rows[0];
  return row && !isTestSubmission(row) ? mapSubmissionToProperty(row) : null;
};

